
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ClientDocument } from '@/components/clients/types';
import { toast } from 'sonner';

export const useClientDocuments = (clientId: string) => {
  return useQuery({
    queryKey: ['client-documents', clientId],
    queryFn: async (): Promise<ClientDocument[]> => {
      const { data, error } = await supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', clientId)
        .order('upload_date', { ascending: false });

      if (error) {
        console.error('Error fetching client documents:', error);
        throw error;
      }

      // Transform the data to match our ClientDocument interface
      return (data || []).map(doc => ({
        ...doc,
        metadata: typeof doc.metadata === 'string' ? JSON.parse(doc.metadata) : doc.metadata || {}
      })) as ClientDocument[];
    },
    enabled: !!clientId,
  });
};

export const useUploadClientDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clientId,
      file,
      documentName,
      documentType,
      category,
      expiryDate,
      isTemplate,
    }: {
      clientId: string;
      file: File;
      documentName: string;
      documentType: ClientDocument['document_type'];
      category?: string;
      expiryDate?: string;
      isTemplate?: boolean;
    }) => {
      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${clientId}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('client-documents')
        .upload(fileName, file);

      if (uploadError) {
        throw uploadError;
      }

      // Map our document types to database enum values with proper typing
      const mapDocumentType = (type: ClientDocument['document_type']): 'agreement' | 'other' | 'legal' | 'financial' | 'compliance' | 'communication' => {
        const typeMapping: Record<string, 'agreement' | 'other' | 'legal' | 'financial' | 'compliance' | 'communication'> = {
          'contract': 'legal',
          'proposal': 'communication',
          'report': 'communication',
          'invoice': 'financial',
          'agreement': 'agreement',
          'other': 'other'
        };
        return typeMapping[type] || 'other';
      };

      // Create document record
      const { data, error } = await supabase
        .from('client_documents')
        .insert({
          client_id: clientId,
          document_name: documentName,
          document_type: mapDocumentType(documentType),
          category,
          file_url: uploadData.path,
          file_size: file.size,
          file_type: file.type,
          expiry_date: expiryDate || null,
          is_template: isTemplate || false,
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['client-documents', data.client_id] });
      toast.success('Document uploaded successfully');
    },
    onError: (error) => {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    },
  });
};

export const useDeleteClientDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clientDocument: ClientDocument) => {
      // Delete file from storage
      if (clientDocument.file_url) {
        const { error: storageError } = await supabase.storage
          .from('client-documents')
          .remove([clientDocument.file_url]);

        if (storageError) {
          console.error('Error deleting file from storage:', storageError);
        }
      }

      // Delete document record
      const { error } = await supabase
        .from('client_documents')
        .delete()
        .eq('id', clientDocument.id);

      if (error) {
        throw error;
      }

      return clientDocument;
    },
    onSuccess: (clientDocument) => {
      queryClient.invalidateQueries({ queryKey: ['client-documents', clientDocument.client_id] });
      toast.success('Document deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    },
  });
};

export const useDownloadClientDocument = () => {
  return useMutation({
    mutationFn: async (clientDocument: ClientDocument) => {
      if (!clientDocument.file_url) {
        throw new Error('No file URL available');
      }

      const { data, error } = await supabase.storage
        .from('client-documents')
        .download(clientDocument.file_url);

      if (error) {
        throw error;
      }

      // Create download link using proper DOM API
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = clientDocument.document_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return data;
    },
    onError: (error) => {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    },
  });
};
